import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { settingsPath } from '@/lib/settingsRoutes'
import { useSubscriptionStatus, type SubscriptionStatus } from '@/lib/subscription'

/**
 * The plan banner (docs/engineering/BILLING.md §In the app).
 *
 *   trial, last 4 days      neutral glass: "3 days left in your free trial"
 *   payment failed (grace)  blush: update the card before it goes read-only
 *   read-only               blush: what happened, and the way out
 *
 * Everyone sees it (Crew included): it never shows a price. The button goes
 * to Settings → Billing for the owner; everyone else is told to ask them.
 * Only the trial heads-up can be dismissed (per session).
 */

export type PlanBannerTone = 'neutral' | 'alert'

export interface PlanBannerModel {
  key: string
  tone: PlanBannerTone
  title: string
  detail: string
  action: string
  dismissible: boolean
}

export function planBannerFor(s: SubscriptionStatus | null, isOwner: boolean): PlanBannerModel | null {
  if (!s) return null
  const ask = 'Ask the account owner to update it in Settings → Billing.'
  switch (s.phase) {
    case 'trialing': {
      if (s.daysLeft === null || s.daysLeft > 3) return null
      const left = s.daysLeft === 0 ? 'Your free trial ends today' : `${s.daysLeft + 1} days left in your free trial`
      return {
        key: `trial-${s.daysLeft}`,
        tone: 'neutral',
        title: left,
        detail: isOwner ? 'Pick a plan to keep everything running without a break.' : 'The account owner can pick a plan in Settings → Billing.',
        action: 'Pick a plan',
        dismissible: true,
      }
    }
    case 'past_due':
      return {
        key: 'past_due',
        tone: 'alert',
        title: 'Your last payment didn’t go through',
        detail: isOwner
          ? `Update your card${s.graceDaysLeft ? ` within ${s.graceDaysLeft} day${s.graceDaysLeft === 1 ? '' : 's'}` : ''} to keep creating and sending.`
          : ask,
        action: 'Update card',
        dismissible: false,
      }
    case 'expired':
    case 'canceled':
    case 'suspended':
    case 'needs_card': {
      const title = s.phase === 'expired' ? 'Your free trial has ended'
        : s.phase === 'canceled' ? 'Your plan has ended'
          : s.phase === 'needs_card' ? 'Add a card to start your free trial'
            : 'Read-only until the payment goes through'
      return {
        key: s.phase,
        tone: 'alert',
        title,
        detail: isOwner
          ? 'You can look things up and export. To create, send and book again, pick a plan.'
          : `You can look things up and export, but not create or send. ${ask}`,
        action: s.phase === 'suspended' ? 'Update card' : s.phase === 'needs_card' ? 'Add card' : 'Pick a plan',
        dismissible: false,
      }
    }
    default:
      return null
  }
}

const DISMISS_KEY = 'plan-banner-dismissed'

export function PlanBanner(): React.ReactElement | null {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const role = useAuthStore((s) => s.user?.role)
  const isOwner = role === 'admin' || role === 'owner'
  const status = useSubscriptionStatus((s) => s.status)
  const refresh = useSubscriptionStatus((s) => s.refresh)
  const [dismissed, setDismissed] = useState<string | null>(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) } catch { return null }
  })

  useEffect(() => {
    if (isAuthenticated) void refresh()
  }, [isAuthenticated, refresh])

  const model = planBannerFor(status, isOwner)
  if (!model || (model.dismissible && dismissed === model.key)) return null
  const alert = model.tone === 'alert'
  const color = alert ? '#F4B5BD' : '#FFFFFF'

  return (
    <div
      role={alert ? 'alert' : 'status'}
      className="mx-4 mt-3 animate-fade-up"
      data-testid="plan-banner"
      style={{
        background: alert ? 'rgba(244,181,189,0.12)' : 'rgba(255,255,255,0.06)',
        border: `0.5px solid ${alert ? 'rgba(244,181,189,0.28)' : 'rgba(255,255,255,0.12)'}`,
        borderRadius: 18,
        padding: '12px 16px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 12, color }}>●</span>
      <div style={{ flex: '1 1 220px', minWidth: 0, fontSize: 14, lineHeight: 1.4, color }}>
        <span style={{ fontWeight: 600 }}>{model.title}.</span>{' '}
        <span style={{ color: alert ? color : 'var(--midnight-text2)' }}>{model.detail}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {isOwner && (
          <button type="button" className="btn-primary" style={{ minHeight: 36, padding: '6px 14px', fontSize: 13 }} onClick={() => navigate(settingsPath('billing'))}>
            {model.action}
          </button>
        )}
        {model.dismissible && (
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => {
              try { sessionStorage.setItem(DISMISS_KEY, model.key) } catch { /* private mode */ }
              setDismissed(model.key)
            }}
            style={{ width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 9999, color: 'var(--midnight-text2)' }}
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  )
}
